import { useEffect, useState, useMemo, cloneElement, isValidElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Play, ArrowRight, Tv, Trophy, Newspaper, Sparkles, Mic, Film, Rocket, Briefcase, Palette, Drama, Compass, TrendingUp, Camera, GraduationCap, Handshake } from 'lucide-react';
import { resolveImageUrl } from '../components/ImageUpload';
import VerifiedCheck from '../components/VerifiedCheck';
import HopeBadge from '../components/HopeBadge';
import AthleteBrandBadges from '../components/AthleteBrandBadges';
import AthleteAvatarFallback, { SCHOOL_PRIMARY_COLOR } from '../components/athlete/AthleteAvatarFallback';
import SchoolBannersRail from '../components/SchoolBannersRail';
import TxHsDistrictsRail from '../components/TxHsDistrictsRail';
import { useChannel } from '../contexts/ChannelContext';
import { useUserAuth } from '../contexts/UserAuthContext';
import PublicHomeLandingBlock from '../components/PublicHomeLandingBlock';
import NetworkVideoModal from '../components/NetworkVideoModal';
import FollowButton from '../components/FollowButton';import PullToRefresh from '../components/network/PullToRefresh';
import HomeLiveGames from '../components/live/HomeLiveGames';
import { FoundingAthletePill, FoundingAthleteModal } from '../components/founding/FoundingAthlete';
import { AppStoreBadge } from '../components/AppStorePromo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTIONS = [
  { id: 'featured', label: 'Featured' },
  { id: 'originals', label: 'Network Originals' },
  { id: 'also_live', label: 'Also Live' },
  { id: 'upcoming', label: 'Upcoming' },
];

// iter238 · "Leagues" rail replaced with LEARN MORE — the focus right
// now is college + HS football, so the bubbles route people to the
// persona pages (athletes, agents, programs, school partnerships)
// instead of league filters. Visionary channel keeps its categories.
const LEARN_MORE = [
  { label: 'Athletes',            icon: Trophy,        slug: 'for-athletes',        to: '/for-athletes' },
  { label: 'Agents',              icon: Briefcase,     slug: 'for-agents',          to: '/for-agents' },
  { label: 'Programs',            icon: GraduationCap, slug: 'for-programs',        to: '/for-programs' },
  { label: 'School Partnerships', icon: Handshake,     slug: 'school-partnerships', to: '/school-partnerships' },
];

// "Dreamers" category badges — clicking filters the athletes grid below by sport.
// Categories filter clientside via `network_category` or matches against `sport`.
const DREAMERS_CATEGORIES = [
  { label: 'Founders',      icon: Rocket,    slug: 'founders' },
  { label: 'CEOs',          icon: Briefcase, slug: 'ceos' },
  { label: 'Entrepreneurs', icon: Trophy,    slug: 'entrepreneurs' },
  { label: 'Artists',       icon: Palette,   slug: 'artists' },
  { label: 'Actors',        icon: Drama,     slug: 'actors' },
  { label: 'Pioneers',      icon: Compass,   slug: 'pioneers' },
  { label: 'Investors',     icon: TrendingUp,slug: 'investors' },
  { label: 'Creators',      icon: Camera,    slug: 'creators' },
  { label: 'All Dreamers',  icon: Film,      slug: 'all',  scroll: true },
];

const showHref = (s) => {
  if (s.link_type === 'athlete') return `/network/${s.link_value}`;
  if (s.link_type === 'story') return `/stories/${s.link_value}`;
  if (s.link_type === 'external') return s.link_value || '#';
  return '#';
};
const showExternal = (s) => s.link_type === 'external';

// iter233 · "Get Access" hero extracted into its own component so it can
// render in two spots: inline on standalone /visionary/network, or injected
// into the compact Homepage flow (below Continue Watching + Trending) for
// signed-in visitors on `/`, per Jay.
const NetworkGetAccessHero = ({ heroAthlete, isVisionary, profileHref }) => (
  <div className="relative overflow-hidden bg-black" data-testid="network-get-access-hero">
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 lg:pt-16 lg:pb-14">
      <div className="inline-flex items-center gap-2 mb-5 px-3 py-1 rounded-full bg-white/5 border border-white/10">
        <Tv className="w-3.5 h-3.5 text-blue-400" />
        <p className="font-display text-[10px] uppercase tracking-widest text-blue-300">
          {isVisionary ? 'Dreamers Network' : 'Dreams League · Network'}
        </p>
      </div>
      {heroAthlete ? (
        <Link to={profileHref(heroAthlete.slug)}
          className="grid md:grid-cols-2 gap-6 lg:gap-10 group" data-testid="network-hero">
          <div className="flex flex-col justify-center order-2 md:order-1">
            <p className="font-display text-xs uppercase tracking-widest text-blue-300 mb-3">
              {heroAthlete.sport}{heroAthlete.team ? ` · ${heroAthlete.team}` : ''} · Network Original
            </p>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95] mb-2 flex items-center gap-3 flex-wrap">
              <span>{heroAthlete.name}</span>
              {heroAthlete.verified && <VerifiedCheck size={28} />}
            </h1>
            {heroAthlete.network_tagline && (
              <p className="font-italic text-lg lg:text-xl text-gray-300 mb-5">{heroAthlete.network_tagline}</p>
            )}
            <p className="text-gray-400 text-base lg:text-lg max-w-md mb-6">
              Step inside their world. Long-form video, brand drops, and the moments no one else captures.
            </p>
            <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-500 hover:bg-blue-400 text-white font-bold transition w-fit">
              <Play className="w-4 h-4 fill-white" /> Get Access
            </span>
          </div>
          <div className="order-1 md:order-2 relative aspect-video md:aspect-square rounded-2xl overflow-hidden bg-white/[0.03] ring-1 ring-white/10 backdrop-blur-md">
            {(heroAthlete.network_hero_image || heroAthlete.photo) ? (
              <img loading="lazy" decoding="async" src={resolveImageUrl(heroAthlete.network_hero_image || heroAthlete.photo, 800)} alt={heroAthlete.name}
                fetchPriority="high"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            ) : null}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500 text-white text-xs font-bold">
              <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> LIVE
            </div>
          </div>
        </Link>
      ) : (
        <p className="text-gray-500 italic py-12">
          {isVisionary
            ? 'No dreamers on the network yet — add some in Admin → Visionaries.'
            : 'No network athletes yet — add some in Admin → Network.'}
        </p>
      )}
    </div>
  </div>
);

// `isHomeMode` is passed by the `/` HomeIndex orchestrator. When true:
//   • The "Follow These Athletes" row is lifted ABOVE the live hero card
//     so it lands at the very top of the home page.
//   • The mid-flow slot where that row used to live now renders the
//     "FLYT BUILT STORIES" headline (previously rendered above by
//     HomeIndex). The standalone `/network` + `/visionary/network`
//     routes (isHomeMode=false) keep their original layout untouched.
const NetworkPage = ({ isHomeMode = false, renderAfterBreaking = null }) => {
  const channel = useChannel();
  const isVisionary = channel.id === 'dreams_visionary';
  const networkBase = isVisionary ? '/visionary/network' : '/network';
  const profileHref = (slug) => `${networkBase}/${slug}`;
  // iter219 · Sign-in awareness. On the `/` home rail we hide the
  // Leagues + Network Originals sections for signed-out visitors —
  // they add noise for someone who's still figuring out what
  // Flythouse is. Signed-in users keep the full network experience.
  const { user } = useUserAuth();
  const isLoggedOut = !user;
  const [shows, setShows] = useState([]);
  const [athletes, setAthletes] = useState([]);
  // iter · Live FLYT IDs (published FLYT ID share pages). Mixed into the
  // "Athletes on the Network" grid so network profiles + live FLYT IDs
  // show together in one clean section (was a separate /discover page).
  const [flytLive, setFlytLive] = useState([]);
  // iter242 · Raw athletes list (no network_active filter) so pinned
  // rotation can still resolve slugs the admin just added but hasn't
  // toggled active yet.
  const [allAthletes, setAllAthletes] = useState([]);
  const [brandNetworks, setBrandNetworks] = useState([]);
  const [followRotation, setFollowRotation] = useState([]);  // iter129
  const [loaded, setLoaded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  // iter229h · Breaking stories rendered as a thin ticker directly
  // ABOVE the Marcus Bell hero for signed-in visitors only. Signed-out
  // visitors don't see this at all (they see the PublicHomeLandingBlock
  // instead of the hero). Lightweight extra fetch — only fires when we
  // actually need the rail.
  const [breakingStories, setBreakingStories] = useState([]);
  useEffect(() => {
    if (!isHomeMode || isLoggedOut) return;
    const params = channel?.id === 'flyt_studios' ? '&channel=flyt_studios' : '';
    axios
      .get(`${API}/stories?breaking=true&limit=6${params}`)
      .then((r) => setBreakingStories(r.data || []))
      .catch(() => {});
  }, [isHomeMode, isLoggedOut, channel?.id]);

  useEffect(() => {
    // Visionaries pull from the separate /api/visionaries collection.
    // Dreams League keeps fetching /api/athletes — slim mode drops
    // heavy timeline/awards/recruiting arrays the rail never renders,
    // cutting the payload ~85%.
    //
    // iter523 · Bumped to `limit=500` (slim mode max_cap) so every
    // athlete Jay pinned into the "Homepage Follow Rotation" resolves,
    // even after the network passes the 25-athlete slim-mode default.
    // Previously the rail silently dropped any pinned slug that fell
    // outside the first 25 returned rows.
    const athletesEndpoint = isVisionary ? `${API}/visionaries` : `${API}/athletes?slim=true&limit=500`;
    const showsEndpoint = isVisionary
      ? `${API}/network/shows?channel=dreams_visionary`
      : `${API}/network/shows`;
    Promise.all([
      axios.get(showsEndpoint).catch(() => ({ data: [] })),
      axios.get(athletesEndpoint).catch(() => ({ data: [] })),
      axios.get(`${API}/brand-networks`).catch(() => ({ data: [] })),
      axios.get(`${API}/stories-layout`).catch(() => ({ data: {} })),
      isVisionary ? Promise.resolve({ data: {} }) : axios.get(`${API}/athletes/flyt-live?limit=100&offset=0`).catch(() => ({ data: {} })),
    ]).then(([s, a, b, layout, fl]) => {
      setShows(s.data || []);
      setFlytLive(Array.isArray(fl?.data?.athletes) ? fl.data.athletes : []);
      // For visionaries, no `network_active` toggle needed yet — show all
      const list = Array.isArray(a.data) ? a.data : [];
      // iter242 · Keep BOTH filtered + raw lists. Filtered drives the
      // main network grid; raw lets pinned rotation resolve every slug
      // even when the athlete isn't `network_active` yet (otherwise
      // pinned athletes silently vanish from the top-of-homepage rail).
      setAllAthletes(list);
      setAthletes(isVisionary ? list : list.filter(x => x.network_active));
      // Only active brand networks, ordered by sort_order.
      const bn = (Array.isArray(b.data) ? b.data : [])
        .filter((x) => x.active !== false)
        .sort((x, y) => (x.sort_order || 0) - (y.sort_order || 0));
      setBrandNetworks(bn);
      // iter129 · Founder-picked homepage Follow Athletes rotation.
      // Dreams League only — visionary channel keeps its own auto row.
      setFollowRotation(isVisionary ? [] : (layout?.data?.follow_athletes_rotation || []));
      setLoaded(true);
    });
  }, [isVisionary]);

  // If URL has #athletes (from "Browse the Network" CTA), scroll there after content loads.
  useEffect(() => {
    if (!loaded) return;
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#athletes') return;
    const id = setTimeout(() => {
      const t = document.getElementById('network-athletes-grid');
      if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(id);
  }, [loaded]);

  const grouped = useMemo(() => {
    const g = {};
    SECTIONS.forEach(s => { g[s.id] = []; });
    shows.forEach(s => { (g[s.section] || g.featured).push(s); });
    Object.values(g).forEach(arr => arr.sort((a, b) => a.sort_order - b.sort_order));
    return g;
  }, [shows]);

  // Hero is the first verified, network-active athlete (Ozzie by default) — clicking
  // "Get Access" takes you into THEIR video-side network page, not a blog story.
  const heroAthlete = athletes[0] || null;
  const featured = (grouped.featured || []).slice(0, 7);

  // iter291 · Founding Athlete banner + modal. Only rendered for the
  // athlete persona so agents/brands/schools don't see it. The
  // network home page also gets a pull-to-refresh wrapper for every
  // persona so the feed can be pulled to reload without leaving.
  const [foundingModalOpen, setFoundingModalOpen] = useState(false);
  // iter312 · Founding Athlete Program is athlete-exclusive marketing.
  // Jay wants this banner hidden from every other persona (agent,
  // brand, program, founder, fan) AND from signed-in users who have
  // not explicitly selected the athlete persona yet. Only render when
  // the active persona is unambiguously 'athlete'.
  const _persona = (user?.persona || '').toLowerCase();
  const showFoundingBanner = isHomeMode && !!user && _persona === 'athlete';
  // iter · Signed-in athletes get the "FLYT Launch Pad" island (their
  // career OS) INSTEAD of the "Find Athletes" circle rail — seeing
  // random athletes doesn't help them build. Everyone else keeps the rail.
  const isAthletePersona = isHomeMode && !!user && _persona === 'athlete';

  // iter · Merge live FLYT IDs into the network-athlete list (dedupe by
  // slug) so the "Athletes on the Network" grid mixes network profiles
  // with live FLYT IDs in one section.
  const networkGridAthletes = (() => {
    const seenSlugs = new Set(athletes.map((a) => a.slug));
    const extras = (flytLive || [])
      .filter((f) => f.slug && !seenSlugs.has(f.slug))
      .map((f) => ({
        ...f,
        // flyt-live payload uses avatar/cover — normalise to the fields
        // the network card renderer reads, and flag so we route to /l/.
        photo: f.photo || f.avatar || null,
        network_hero_image: f.network_hero_image || f.cover || null,
        __flytLive: true,
      }));
    return [...extras, ...athletes];
  })();

  return (
    <PullToRefresh disabled={!isHomeMode}>
    <div className="min-h-screen bg-black text-white" data-testid="network-page">
      {/* iter55 · The Pulse now lives in the top nav as a slide-down pill,
          so the standalone strip that used to sit here was removed to keep
          the homepage focused on Follow These Athletes → Hero → content. */}

      {/* HOME-MODE TOP: FOLLOW THESE ATHLETES — lifted ABOVE the live
          hero card on `/` so the first thing visitors see is the
          athlete avatar row they can tap to connect. Standalone
          `/network` keeps the row in its original mid-page slot.
          iter222 · Also visible to signed-out visitors — Jay wants
          this rail above the Ozzie hero for everyone so the "real
          athletes on the network" signal reads first. */}
      {/* iter848 · Jay: embedding this (isHomeMode) inside AthleteHome's
          collapsible "See full network" section duplicated the athlete's
          own Launch Pad — AthleteHome already shows one above this
          section. Find Athletes rail stays hidden for athlete persona;
          nothing replaces it here anymore. */}
      {isHomeMode && athletes.length > 0 && !isAthletePersona && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6">
          <FollowAthletesRow
            athletes={athletes}
            allAthletes={allAthletes}
            networkBase={networkBase}
            rotation={followRotation}
          />
        </div>
      )}

      {/* iter695 · LIVE NOW strip — below "Find Athletes". Renders every
          college-football game in progress right now (onboarded Texas
          schools pinned first). Tapping a card slides up the full
          Liquid-Glass LiveGamePill + live play-by-play cast. The whole
          section self-hides when nothing is live. */}
      {isHomeMode && (
        <div className="max-w-7xl mx-auto lg:px-8">
          <HomeLiveGames />
        </div>
      )}

      {/* iter229h · Breaking-news ticker — sits right above the Marcus
          Bell hero for signed-in visitors only, per Jay. Renders nothing
          when logged out or when there aren't any breaking stories. */}
      {isHomeMode && !isLoggedOut && breakingStories.length > 0 && (
        <div className="border-y border-white/10 bg-black/90 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
            <div className="flex items-center gap-5 overflow-x-auto scrollbar-hide">
              <span className="inline-flex items-center gap-1.5 font-display text-[10px] uppercase whitespace-nowrap tracking-widest text-red-400">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                Breaking
              </span>
              {breakingStories.map((story, idx) => (
                <Link
                  key={story.id}
                  to={`${channel.storiesPath || '/stories'}/${story.slug}`}
                  className="font-body text-sm whitespace-nowrap text-white/85 hover:text-blue-300 transition"
                  data-testid={`network-breaking-${idx}`}
                >
                  {story.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* iter229p · Slot for the featured story hero + Top Stories
          column. HomeIndex plumbs `<Homepage compact />` in here so
          the running order for signed-in visitors is:
             Find Athletes → BREAKING ticker → Featured Story hero
             (The Goal of Flythouse) → Continue Watching → Trending →
             Marcus Bell / Get Access rotator → rest of Network.
          iter233 · The Get Access hero no longer renders inline below
          this slot on `/` — it's injected INTO the compact Homepage
          (right under Continue Watching + Trending) via the
          `networkHeroSlot` prop, per Jay. */}
      {renderAfterBreaking && isValidElement(renderAfterBreaking)
        ? cloneElement(renderAfterBreaking, {
            hideDiscovery: isAthletePersona,
            networkHeroSlot: (
              <NetworkGetAccessHero
                heroAthlete={heroAthlete}
                isVisionary={isVisionary}
                profileHref={profileHref}
              />
            ),
          })
        : renderAfterBreaking}

      {/* HERO — pure black surface, no glows, no gradients. Keeps the
          transition from the athlete rail above and into the section
          below completely seamless.
          iter233 · Inline render is now standalone-only (/visionary/network).
          On `/` (home mode): signed-out visitors get `PublicHomeLandingBlock`
          instead, signed-in visitors get the hero injected into the compact
          Homepage flow above. */}
      {!isHomeMode && (
        <NetworkGetAccessHero
          heroAthlete={heroAthlete}
          isVisionary={isVisionary}
          profileHref={profileHref}
        />
      )}

      {/* iter220 · SIGNED-OUT LANDING BLOCK — sits immediately below
          the hero for new visitors. Real product mockups (live
          iframes of a Flythouse profile + bio-link), "Your career
          deserves more than a link" pitch, and a big "Make mine now"
          CTA. Signed-in users skip this because they've already
          bought in — they get the athletes rail here instead. */}
      {isHomeMode && isLoggedOut && <PublicHomeLandingBlock />}

      {/* iter222 · Removed the duplicate FollowAthletesRow that
          iter220 rendered below the landing block. The top rail
          above the Ozzie hero is now shown to everyone (see the
          earlier `FollowAthletesRow` block near the top of this
          component) so no second render is needed. */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 space-y-14">

        {/* MID-FLOW SLOT — on `/` this is where the FLYT BUILT STORIES
            section heading lives (replacing the FollowAthletesRow that
            moved to the top of the page in home mode). On standalone
            /network this still renders the FollowAthletesRow inline.
            Negative top margin pulls the title up tight against the
            hero above, and tight internal padding keeps the gap
            before the FEATURED row small. */}
        {isHomeMode ? (
          <header className="-mt-8 sm:-mt-10 pt-0 pb-0" data-testid="flyt-built-stories-header">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-white">
              Flyt built stories
            </h2>
            <p className="mt-2 sm:mt-3 font-body text-base sm:text-lg lg:text-xl text-white/70 italic">
              Where athletes build their story and the world follows.
            </p>
          </header>
        ) : (
          athletes.length > 0 && (
            <FollowAthletesRow
              athletes={athletes}
              allAthletes={allAthletes}
              networkBase={networkBase}
              rotation={followRotation}
            />
          )
        )}

        {/* FEATURED ROW — hidden for signed-in athletes (they get the
            Stories swipe + Top Stories block above instead), per Jay. */}
        {featured.length > 0 && !isAthletePersona && (
          <Section title="Featured">
            <ShowRow items={featured} athletes={athletes} networkBase={networkBase} />
          </Section>
        )}

        {/* BRAND SERIES — clickable brand network tiles. Routes into each
            brand's dedicated /brand/<slug> page (the same destination
            the HOPE pill on an athlete card hits). Only renders when at
            least one active brand network exists. */}
        {brandNetworks.length > 0 && !isAthletePersona && (
          <BrandSeriesRow brands={brandNetworks} />
        )}

        {/* iter227 · Removed the "Home of Access to Athletes" banner
            on request. It appeared in both logged-in and logged-out
            states and duplicated messaging already carried by the
            landing hero and channel copy. */}

        {/* LEARN MORE / DREAMERS row — channel-aware.
            iter219 · Hidden on the network home for signed-out visitors
            so the landing pitch below carries the message instead.
            iter238 · Leagues bubbles became persona "Learn More" links
            for signed-in visitors who want the deeper pitch. */}
        {!(isHomeMode && isLoggedOut) && !isAthletePersona && (
        <Section title={isVisionary ? 'Dreamers' : 'Learn More'}>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
            {(isVisionary ? DREAMERS_CATEGORIES : LEARN_MORE).map((l) => {
              const Icon = l.icon;
              const isActive = isVisionary && selectedCategory === l.slug;
              const handleClick = (e) => {
                if (!isVisionary) return;
                e.preventDefault();
                setSelectedCategory(l.scroll ? 'all' : l.slug);
                const target = document.getElementById('network-athletes-grid');
                if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              };
              const to = isVisionary ? '#' : l.to;
              return (
                <Link key={l.label}
                  to={to}
                  onClick={handleClick}
                  className="group flex-shrink-0 w-[100px] flex flex-col items-center gap-2.5"
                  data-testid={`league-${l.slug}`}>
                  <div className={`w-16 h-16 rounded-full border flex items-center justify-center transition ${
                    isActive
                      ? 'bg-white border-white'
                      : 'bg-white/5 border-white/10 group-hover:border-blue-400/60 group-hover:bg-white/10'
                  }`}>
                    <Icon className={`w-6 h-6 transition ${
                      isActive ? 'text-black' : 'text-blue-300 group-hover:text-blue-400'
                    }`} />
                  </div>
                  <p className={`text-xs text-center transition ${
                    isActive ? 'text-white font-bold' : 'text-gray-300 group-hover:text-white'
                  }`}>{l.label}</p>
                </Link>
              );
            })}
          </div>
        </Section>
        )}

        {/* NETWORK ORIGINALS / ATHLETE HOMES
            iter219 · Hidden on the network home for signed-out
            visitors — this is a deeper-catalog section that resonates
            after someone knows what Flythouse is. */}
        {grouped.originals.length > 0 && !(isHomeMode && isLoggedOut) && (
          <Section title="Network Originals">
            <ShowRow items={grouped.originals} athletes={athletes} networkBase={networkBase} />
          </Section>
        )}

        {/* Phase 1 · Roster Sync — school-branded banner rail. Moved to
            live directly under Network Originals per launch feedback:
            gives visitors a program-level pivot into the full roster
            without cluttering the athlete carousel above. */}
        {isHomeMode && !isAthletePersona && (
          <Section title={isVisionary ? 'Programs' : 'Texas College Football'} eyebrow="Browse Teams">
            <SchoolBannersRail
              title={null}
              kicker={null}
              type="college"
            />
          </Section>
        )}

        {/* iter314 · Texas High School Football section — tile shows
            DISTRICT icons (Frisco, Plano, Prosper, Allen, Southlake,
            McKinney, Highland Park, Duncanville, DeSoto). Each district
            drills into /texas-hs/{district-slug} which lists that ISD's
            schools using the existing SchoolBannersRail component.
            Claim + verify flow on each school stays identical to the
            college programs. */}
        {isHomeMode && !isVisionary && !isAthletePersona && (
          <Section title="Texas High School Football" eyebrow="Browse Districts">
            <TxHsDistrictsRail />
          </Section>
        )}

        {/* ATHLETE HOMES — pulled directly from athletes with network_active.
            On Dreamers, also filtered by selected category (sport/role keywords).
            Homepage view is capped to 10 athletes so the grid stays curated;
            the full DB (with filters) lives at /athletes.
            iter849 · Jay: hide this specifically for the embedded-athlete
            case (AthleteHome's "See full network" section) — not enough
            onboarded athletes yet for it to look right there. Every other
            caller (standalone /network, signed-out `/`) is unaffected. */}
        {networkGridAthletes.length > 0 && !isAthletePersona && (
          <Section
            title={isVisionary ? 'Dreamers on the Network' : 'Athletes on the Network'}
            subtitle="Tap to enter their world"
          >
            <div id="network-athletes-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 scroll-mt-24">
              {(() => {
                // Filter visionary athletes by selectedCategory (matches against sport/team text)
                const matches = (a) => {
                  if (!isVisionary || selectedCategory === 'all') return true;
                  const hay = `${a.sport || ''} ${a.team || ''} ${(a.network_category || '')}`.toLowerCase();
                  // Plural slug → singular root for matching (founders -> founder, ceos -> ceo)
                  const root = selectedCategory.replace(/s$/, '');
                  return hay.includes(root);
                };
                // Homepage cap: keep the curated top 10 and route deeper browsing
                // to /discover. On the standalone /network page (isHomeMode = false)
                // we still show the full list.
                const filtered = networkGridAthletes.filter(matches);
                const visible = isHomeMode ? filtered.slice(0, 10) : filtered;
                if (visible.length === 0) {
                  return (
                    <p className="col-span-full text-gray-500 italic py-8">
                      No dreamers in this category yet — try another or browse all.
                    </p>
                  );
                }
                return visible.map((a) => {
                const bgColor = a.school_primary_color || '#111827';
                return (
                <Link key={a.slug} to={a.__flytLive ? `/l/${a.slug}` : profileHref(a.slug)}
                  className="group relative aspect-[3/4] rounded-2xl overflow-hidden ring-1 ring-white/10 hover:ring-blue-400/60 transition-all hover:-translate-y-1"
                  style={{
                    background: (a.network_hero_image || a.photo) ? undefined
                      : `radial-gradient(circle at 30% 20%, ${bgColor}66 0%, ${bgColor}26 45%, #050505 100%)`,
                    // iter239 · School-color ring on photo-less tiles so
                    // unclaimed athletes read branded, not black-boxed.
                    boxShadow: (a.network_hero_image || a.photo) ? undefined : `inset 0 0 0 1.5px ${bgColor}99`,
                  }}
                  data-testid={`network-athlete-${a.slug}`}>
                  {/* Follow / Get Updates — sits top-right on the card
                      so visitors can subscribe without leaving the grid.
                      FollowButton internally stopPropagation's the click
                      so the outer Link doesn't fire. */}
                  <div className="absolute top-2 right-2 z-10">
                    <FollowButton athleteId={a.id} variant="icon" />
                  </div>
                  {(a.network_hero_image || a.photo) ? (
                    <img src={resolveImageUrl(a.network_hero_image || a.photo, 400)} alt={a.name}
                      loading="lazy" decoding="async"
                      className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <AthleteAvatarFallback athlete={a} size={110} ringWidth={3} />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-4">
                    <p className="font-display text-[10px] uppercase tracking-widest text-blue-300 mb-1">{a.sport}{a.team ? ` · ${a.team}` : ''}</p>
                    <p className="font-display text-lg uppercase leading-tight text-white flex items-center gap-1.5">
                      {a.name}
                      {a.verified && <VerifiedCheck size={16} />}
                    </p>
                    {a.network_tagline && <p className="text-xs text-gray-300 mt-1 line-clamp-2">{a.network_tagline}</p>}
                  </div>
                </Link>
              );
              });
              })()}
            </div>
            {/* Homepage "See More" ⇒ full DB with filters */}
            {isHomeMode && networkGridAthletes.length > 10 && (
              <div className="mt-6 flex flex-col items-center gap-3">
                <Link
                  to="/discover"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-sm font-bold uppercase tracking-widest hover:bg-blue-100 transition"
                  data-testid="athletes-grid-see-more"
                >
                  See all athletes <ArrowRight className="w-4 h-4" />
                </Link>
                <AppStoreBadge variant="dark" size="sm" testid="network-home-app-store-badge" />
              </div>
            )}
          </Section>
        )}

        {/* ALSO LIVE */}
        {grouped.also_live.length > 0 && (
          <Section title="Also Live">
            <ShowRow items={grouped.also_live} athletes={athletes} networkBase={networkBase} />
          </Section>
        )}

        {/* UPCOMING */}
        {grouped.upcoming.length > 0 && (
          <Section title="Upcoming">
            <ShowRow items={grouped.upcoming} athletes={athletes} networkBase={networkBase} />
          </Section>
        )}

        {!loaded && <p className="text-gray-500 text-center py-12">Loading…</p>}
      </div>
      <FoundingAthleteModal
        open={foundingModalOpen}
        onClose={() => setFoundingModalOpen(false)}
      />
    </div>
    </PullToRefresh>
  );
};

// === Helpers ===

const Section = ({ title, eyebrow, subtitle, action, children }) => (
  <section>
    <div className="flex items-end justify-between mb-5 gap-3">
      <div>
        {eyebrow && <p className="font-italic text-blue-300 text-xs uppercase tracking-widest mb-1.5">{eyebrow}</p>}
        <h2 className="font-display text-xl md:text-2xl uppercase tracking-wider text-white">{title}</h2>
        {subtitle && <p className="font-italic text-blue-300 text-xs uppercase tracking-widest mt-1.5">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
    {children}
  </section>
);

// ============ FOLLOW THESE ATHLETES ROW ============
// Horizontal scroll of circular athlete avatars — sits above the Featured
// shows on /network so visitors can connect with athletes in one tap. The
// "See all" pill smooth-scrolls to the full athlete grid further down.
const FollowAthletesRow = ({ athletes, allAthletes = null, networkBase = '/network', rotation = [] }) => {
  // "New on the Network" badge — anyone with a `pushed_to_network_at`
  // (or `network_verified_at`) timestamp inside the last 72h earns a
  // green NEW pill on their tile. Keeps the home rail feeling live
  // without adding a separate "Just added" section.
  const NEW_WINDOW_MS = 72 * 60 * 60 * 1000;
  const isNew = (a) => {
    const raw = a.pushed_to_network_at || a.network_verified_at;
    if (!raw) return false;
    const t = new Date(raw).getTime();
    if (!Number.isFinite(t)) return false;
    return (Date.now() - t) <= NEW_WINDOW_MS;
  };
  // iter129 · Founder-picked rotation takes total priority. When the
  // Admin → Stories Page Layout → "Homepage Follow Rotation" list has
  // any slugs set, the row renders EXACTLY those athletes in that
  // order. Newly pushed athletes still land in the network directory
  // but stop hijacking the top-of-homepage rail. Empty rotation →
  // legacy auto-curated behavior below.
  //
  // iter242 · Cap removed — used to slice at 5. The admin now supports
  // up to 60 athletes and the rail scrolls horizontally, so every
  // pinned athlete appears in the exact order Jay dropped them.
  const items = (() => {
    const list = athletes || [];
    // iter242 · Resolve pinned slugs from the unfiltered `allAthletes`
    // list so an athlete that Jay pinned before flipping `network_active`
    // still shows up. Falls back to the regular list if raw not passed.
    const pool = (allAthletes && allAthletes.length ? allAthletes : list);
    if (Array.isArray(rotation) && rotation.length > 0) {
      const bySlug = new Map(pool.map((a) => [a.slug, a]));
      const picked = rotation
        .map((slug) => bySlug.get(slug))
        .filter(Boolean);
      if (picked.length > 0) return picked;
      // fall through to auto-curation if none of the picked slugs
      // resolved (e.g. the athletes got removed from the network).
    }
    const KNOWN_SCHOOLS = new Set([
      'texas-longhorns', 'texas-am-aggies', 'smu-mustangs',
      'baylor-bears', 'texas-tech-red-raiders', 'tcu-horned-frogs',
    ]);
    const isTestAthlete = (a) => {
      const n = (a.name || '').trim();
      if (!n || n.length < 4) return true;                       // "B" / "AJ" style stubs
      if (/^test\b|^ui\b|^athlete\b|^b\s|^bulk/i.test(n)) return true;
      return false;
    };
    const curated = list.filter((a) => (a.photo || a.network_hero_image) && !isTestAthlete(a));
    // Sort: new athletes first (most recent first), then everyone else.
    curated.sort((a, b) => {
      const na = isNew(a) ? 1 : 0;
      const nb = isNew(b) ? 1 : 0;
      if (na !== nb) return nb - na;
      if (na && nb) {
        const ta = new Date(a.pushed_to_network_at || a.network_verified_at || 0).getTime();
        const tb = new Date(b.pushed_to_network_at || b.network_verified_at || 0).getTime();
        return tb - ta;
      }
      return 0;
    });
    const curatedTop = curated.slice(0, 4);
    const synced  = list.filter((a) =>
      !a.photo && !a.network_hero_image
      && KNOWN_SCHOOLS.has(a.school_slug)
      && !isTestAthlete(a)
      && a.sport === 'Football'
    );
    const seen = new Set();
    const oneEach = [];
    for (const a of synced) {
      if (!seen.has(a.school_slug)) {
        seen.add(a.school_slug);
        oneEach.push(a);
      }
    }
    return [...curatedTop, ...oneEach].slice(0, 10);
  })();
  return (
    <section data-testid="follow-athletes-row">
      <div className="flex items-end justify-between mb-4">
        <div>
          {/* iter229d · Removed the "On the Network" eyebrow per Jay —
              the H2 "Find Athletes" carries enough meaning on its own. */}
          <h2 className="font-display text-xl md:text-2xl uppercase tracking-wider text-white">
            Find Athletes
          </h2>
        </div>
        {/* iter241 · "See more" now jumps to the search tab so athletes
            can find themselves + fans can hunt by name. The rail above
            stays a curated launch pad; search is where discovery lives. */}
        <Link
          to="/search"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/15 text-white text-[11px] font-bold uppercase tracking-wider ring-1 ring-white/10 transition"
          data-testid="follow-athletes-see-more"
        >
          See more <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="flex gap-3 sm:gap-4 overflow-x-auto pt-1 pb-6 -mx-1 px-1 snap-x snap-mandatory no-scrollbar">
        {items.map(a => {
          const photo = a.network_hero_image || a.photo;
          return (
            <Link
              key={a.id || a.slug}
              to={`${networkBase}/${a.slug}`}
              className="group flex-shrink-0 w-[78px] sm:w-[88px] snap-start text-center"
              data-testid={`follow-athlete-${a.slug}`}
            >
              <div className="relative mx-auto w-[68px] h-[68px] sm:w-[76px] sm:h-[76px]">
                {/* School-badge fallback always sits underneath; if the
                    photo 404s (stale seed URLs) onError hides the img so
                    the badge shows instead of broken alt text. */}
                <div className="absolute inset-0">
                  <AthleteAvatarFallback athlete={a} className="w-full h-full" />
                </div>
                {photo && (
                  <img
                    src={resolveImageUrl(photo, 400)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="relative w-full h-full rounded-full object-cover ring-2 ring-white/20 group-hover:ring-blue-400 transition"
                  />
                )}
                {/* NEW pill — only appears for the first 3 days after an
                    athlete joins the Network. Positioned top-center so
                    it clears the verified check (bottom-right) and any
                    brand badge on the right edge. */}
                {isNew(a) && (
                  <span
                    className="absolute -top-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-emerald-500 text-black text-[8px] font-black uppercase tracking-widest ring-2 ring-black shadow-lg"
                    data-testid={`follow-athlete-new-${a.slug}`}
                  >
                    New
                  </span>
                )}
                {a.verified && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-blue-500 ring-2 ring-black flex items-center justify-center">
                    <VerifiedCheck size={10} />
                  </span>
                )}
                {a.hope && (
                  <HopeBadge
                    size="sm"
                    className={a.verified ? '-bottom-1.5 right-3' : ''}
                  />
                )}
                {/* Generic brand-network pills (HOPE excluded when the
                    legacy boolean is rendering it above). When verified,
                    nudge the first pill ~14px left so it clears the
                    blue-check dot in the bottom-right corner. */}
                <AthleteBrandBadges
                  athlete={a}
                  size="sm"
                  exclude={a.hope ? ['hope'] : []}
                  offsetRight={a.verified ? 18 : 4}
                  bottomClass={a.verified ? '-bottom-1.5' : '-bottom-1'}
                />
              </div>
              <p className="mt-2 text-[11px] font-bold text-white leading-tight line-clamp-1 group-hover:text-blue-300 transition">
                {a.name?.split(' ')[0] || a.name}
              </p>
              {a.sport && (
                <p className="text-[9px] uppercase tracking-wider text-gray-500 line-clamp-1">{a.sport}</p>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
};

const ShowRow = ({ items, athletes = [], networkBase = '/network' }) => {
  const navigate = useNavigate();
  const [modalVideo, setModalVideo] = useState(null);
  const [modalAthlete, setModalAthlete] = useState(null);

  // Find a network-active athlete that matches this show by name/slug. Used
  // to wire the modal's "Follow / Go to Profile" CTAs.
  const matchAthlete = (s) => {
    if (!athletes?.length) return null;
    const title = (s.title || '').toLowerCase();
    const linkVal = (s.link_value || '').toLowerCase();
    const subtitle = (s.subtitle || '').toLowerCase();
    return athletes.find((a) => {
      const name = (a.name || '').toLowerCase();
      const slug = (a.slug || '').toLowerCase();
      if (!name && !slug) return false;
      return (
        (name && (title.includes(name) || subtitle.includes(name))) ||
        (slug && (linkVal === slug || linkVal.includes(slug)))
      );
    }) || null;
  };

  // When the show points to a story, fetch that story and open the modal
  // instead of navigating away. External + raw-athlete links keep the
  // existing in-place navigation.
  const handleShowClick = async (e, s) => {
    if (showExternal(s)) return; // let the <a> handle it
    if (s.link_type === 'athlete') return; // direct nav to athlete page
    if (s.link_type !== 'story' || !s.link_value) return; // fallback nav
    e.preventDefault();
    try {
      const r = await axios.get(`${API}/stories/${s.link_value}`);
      const story = r.data || {};
      const a = matchAthlete(s);
      setModalAthlete(a);
      setModalVideo({
        title: s.title || story.name,
        subtitle: s.subtitle || story.subtitle,
        video_url: story.video_url,
        poster: s.poster || story.cover_image || story.thumbnail,
        body: story.summary || (story.article_body || '').slice(0, 600),
      });
    } catch (err) {
      console.debug('[ShowRow] story fetch failed, falling back to nav', err?.message);
      navigate(`${networkBase}/${(matchAthlete(s) || {}).slug || ''}`);
    }
  };

  // Build name→slug map of network-active athletes so featured/originals/etc.
  // route to the channel-appropriate Network athlete page (not the blog).
  const resolveHref = (s) => {
    if (showExternal(s)) return s.link_value || '#';
    if (s.link_type === 'athlete' && s.link_value) return `${networkBase}/${s.link_value}`;
    const match = matchAthlete(s);
    if (match) return `${networkBase}/${match.slug}`;
    if (s.link_type === 'story') return networkBase;
    return showHref(s);
  };

  return (
    <>
    <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory">
      {items.map((s) => {
        const href = resolveHref(s);
        const isStoryClick = s.link_type === 'story' && s.link_value && !showExternal(s);
        const Tag = showExternal(s) ? 'a' : Link;
        const props = showExternal(s)
          ? { href, target: '_blank', rel: 'noopener noreferrer' }
          : { to: href, onClick: (e) => handleShowClick(e, s) };
        return (
          <Tag key={s.id} {...props}
            className="group flex-shrink-0 w-[200px] sm:w-[240px] lg:w-[280px] snap-start cursor-pointer"
            data-testid={`network-show-${s.id}`}>
            <div className="relative aspect-video rounded-xl overflow-hidden bg-gradient-to-br from-gray-800 to-black ring-1 ring-white/10 group-hover:ring-blue-400/60 transition">
              {s.poster ? (
                <img src={resolveImageUrl(s.poster, 400)} alt={s.title}
                  loading="lazy" decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              ) : null}
              {s.badge && (
                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                  s.badge.toLowerCase().includes('break') ? 'bg-red-500 text-white' :
                  s.badge.toLowerCase() === 'live' || s.badge.toLowerCase() === 'recent' ? 'bg-red-500 text-white' :
                  'bg-white text-black'
                }`}>
                  {(s.badge.toLowerCase() === 'live' || s.badge.toLowerCase() === 'recent') && <span className="inline-block w-1.5 h-1.5 rounded-full bg-white animate-pulse mr-1 align-middle" />}
                  {s.badge.toLowerCase() === 'live' ? 'Recent' : s.badge}
                </div>
              )}
              {isStoryClick && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                  <span className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center">
                    <Play className="w-5 h-5 fill-black text-black ml-0.5" />
                  </span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition" />
            </div>
            <p className="mt-2.5 text-sm font-bold text-white line-clamp-2 leading-tight">{s.title}</p>
            {s.subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{s.subtitle}</p>}
          </Tag>
        );
      })}
    </div>
    <NetworkVideoModal
      video={modalVideo}
      athlete={modalAthlete}
      variant="wide"
      onClose={() => { setModalVideo(null); setModalAthlete(null); }}
    />
    </>
  );
};

// ============ BRAND SERIES ROW ============
// Horizontal scroll of brand-network tiles — clicking a tile lands the
// user on the brand's dedicated /brand/<slug> page (same destination as
// the small "HOPE" pill on athlete cards). Each tile fills with the
// brand's accent → secondary gradient, shows the logo if uploaded, and
// falls back to the brand name in big Anton display type otherwise.
const BrandSeriesRow = ({ brands }) => {
  const items = (brands || []).slice(0, 24);
  if (!items.length) return null;

  return (
    <section data-testid="brand-series-row">
      <div className="flex items-end justify-between mb-5">
        <div>
          <p className="font-italic text-blue-300 text-xs uppercase tracking-widest mb-1.5">
            On the Network
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-white">
            Brand Series
          </h2>
          <p className="mt-2 sm:mt-3 font-body text-sm sm:text-base text-white/60 italic">
            Athlete stories built with the brands behind them.
          </p>
        </div>
      </div>

      <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory no-scrollbar">
        {items.map((b) => {
          const accent = b.accent_color || '#22D3EE';
          const secondary = b.secondary_color || '#EC4899';
          const text = b.text_color || '#FFFFFF';
          const hasLogo = !!b.logo;
          const hasHero = !!b.hero_image;

          return (
            <Link
              key={b.id || b.slug}
              to={`/brand/${b.slug}`}
              className="group flex-shrink-0 w-[200px] sm:w-[240px] lg:w-[280px] snap-start"
              data-testid={`brand-series-${b.slug}`}
              aria-label={`Open ${b.name} brand series`}
            >
              <div
                className="relative aspect-video rounded-xl overflow-hidden ring-1 ring-white/10 group-hover:ring-white/40 transition shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]"
                style={{
                  background: hasHero
                    ? '#000'
                    : `linear-gradient(135deg, ${accent} 0%, ${secondary} 100%)`,
                }}
              >
                {/* Optional background hero photo with darken overlay */}
                {hasHero && (
                  <>
                    <img
                      src={resolveImageUrl(b.hero_image, 800)}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(135deg, ${accent}55 0%, ${secondary}88 100%)`,
                        mixBlendMode: 'multiply',
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
                  </>
                )}

                {/* Subtle radial sheen so the tile has glass depth */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(circle at 25% 20%, rgba(255,255,255,0.18), transparent 55%)',
                  }}
                />

                {/* Centered brand mark — logo image if present, otherwise
                    the name set in big Anton uppercase. */}
                <div className="absolute inset-0 flex items-center justify-center p-4">
                  {hasLogo ? (
                    <img
                      src={resolveImageUrl(b.logo, 400)}
                      alt={b.name}
                      loading="lazy"
                      decoding="async"
                      className="max-w-[70%] max-h-[60%] object-contain drop-shadow-[0_4px_18px_rgba(0,0,0,0.35)]"
                    />
                  ) : (
                    <span
                      className="font-display uppercase leading-none text-3xl sm:text-4xl lg:text-5xl tracking-tight text-center drop-shadow-[0_4px_18px_rgba(0,0,0,0.35)]"
                      style={{ color: text }}
                    >
                      {b.name}
                    </span>
                  )}
                </div>

                {/* Bottom-left "SERIES" chip — IG-style glass pill that
                    signals the tile is clickable into a brand world. */}
                <div className="absolute left-2 bottom-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/45 backdrop-blur ring-1 ring-white/20">
                  <Sparkles className="w-2.5 h-2.5 text-white/85" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-white/90">
                    Series
                  </span>
                </div>
              </div>

              <p className="mt-2.5 text-sm font-bold text-white line-clamp-1 leading-tight">
                {b.name}
              </p>
              {b.tagline && (
                <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">{b.tagline}</p>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default NetworkPage;
